import { Card, Skeleton } from '@parrot-software/design-system';
import { useTranslation } from 'react-i18next';

import { isOfflineFailure } from '@identity/application/errors';
import { ErrorRetryCard } from '@identity/presentation/components/base';
import { ProfileContactCard, ProfilePasswordCard } from '@identity/presentation/components/specific';
import { IdentityFlowProvider } from '@identity/presentation/context';
import { useAccountStatus } from '@identity/presentation/hooks';

/** The skeleton of a card. The frame is already painted, so a full screen loader would flash it. */
const CardSkeleton = () => (
  <Card
    className="flex w-full flex-col gap-4 border-0 bg-transparent p-0 font-sans shadow-none md:border md:bg-card md:p-6 md:shadow-xs"
    data-testid="profile-card-skeleton"
  >
    <Skeleton className="h-5 w-40" />
    <Skeleton className="h-4 w-72" />
    <Skeleton className="h-10 w-full" />
    <Skeleton className="h-10 w-full" />
  </Card>
);

const ProfileContent = () => {
  const { t } = useTranslation('identity');
  const { data, error, isLoading, refetch } = useAccountStatus();

  const name = data ? `${data.account.firstName} ${data.account.lastName ?? ''}`.trim() : '';

  const renderCards = () => {
    if (error) {
      const copyKey = isOfflineFailure(error) ? 'profile.error.offline' : 'profile.error';

      return (
        <ErrorRetryCard
          actionLabel={t('profile.error.action')}
          body={t(`${copyKey}.body`)}
          onRetry={() => refetch()}
          title={t(`${copyKey}.title`)}
        />
      );
    }

    if (isLoading || !data) {
      return (
        <>
          <CardSkeleton />
          <CardSkeleton />
        </>
      );
    }

    // The password card travels inside the contact card so the submit button and its note close the
    // screen, below both cards, instead of splitting them.
    return (
      <ProfileContactCard email={data.account.email} phone={data.account.phone} requirements={data.requirements}>
        <ProfilePasswordCard passwordChangedAt={data.account.passwordChangedAt} />
      </ProfileContactCard>
    );
  };

  return (
    <div className="flex w-full flex-col gap-6 p-4 font-sans max-[599px]:mt-14 md:p-6">
      <div className="flex flex-col gap-1">
        <p className="text-sm text-foreground">{t('profile.eyebrow')}</p>
        <h1 className="text-2xl font-semibold text-foreground">{name}</h1>
        <p className="text-sm text-muted-foreground">{t('profile.subtitle')}</p>
      </div>
      {renderCards()}
    </div>
  );
};

/**
 * Mi perfil: the only screen of this domain a user reaches because they decided to.
 *
 * It mounts inside the application frame and shows no store selector, because a profile belongs to
 * no store. Any valid session enters: there is no permission to check.
 *
 * The name comes from the account status and not from the session, although both hold it. The screen
 * already reads the status for the badges and the date, and a second source for the same value can
 * only ever disagree.
 */
export const ProfilePage = () => (
  <IdentityFlowProvider flow="profile">
    <ProfileContent />
  </IdentityFlowProvider>
);
